import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../services/api';

const SchoolProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [schools, setSchools] = useState([]);
  const [formData, setFormData] = useState({
    project_name: '',
    school_id: '',
    lab_size: 0,
    total_computers: 0,
    estimated_cost: 0,
    status: 'pending',
    start_date: dayjs().format('YYYY-MM-DD'),
    estimated_completion_date: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    notes: ''
  });

  const fetchSchools = useCallback(async () => {
    try {
      const response = await api.getSchools();
      setSchools(response.data || []);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError('Failed to load schools');
    }
  }, []);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getSchoolProjectById(id);
      const project = response.data;
      
      setFormData({
        project_name: project.project_name || '',
        school_id: project.school_id || '',
        lab_size: project.lab_size || 0,
        total_computers: project.total_computers || 0,
        estimated_cost: project.estimated_cost || 0,
        status: project.status || 'pending',
        start_date: project.start_date ? dayjs(project.start_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        estimated_completion_date: project.estimated_completion_date ? dayjs(project.estimated_completion_date).format('YYYY-MM-DD') : dayjs().add(30, 'day').format('YYYY-MM-DD'),
        notes: project.notes || ''
      });
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSchools();
    
    if (isEditMode) {
      fetchProject();
    }
  }, [fetchSchools, fetchProject, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData
      };

      if (isEditMode) {
        await api.updateSchoolProject(id, payload);
        setSuccess('Project updated successfully!');
      } else {
        await api.createSchoolProject(payload);
        setSuccess('Project created successfully!');
      }

      setTimeout(() => {
        navigate('/school-projects/projects');
      }, 1500);

    } catch (err) {
      setError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit School Project' : 'Create New School Project'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name"
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>School</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id}
                  onChange={handleChange}
                  label="School"
                >
                  <MenuItem value="">Select School</MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.school_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Lab Size (Number of Computers)"
                name="lab_size"
                type="number"
                value={formData.lab_size}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Total Computers"
                name="total_computers"
                type="number"
                value={formData.total_computers}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="installed">Installed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estimated Cost (Rs)"
                name="estimated_cost"
                type="number"
                value={formData.estimated_cost}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estimated Completion Date"
                name="estimated_completion_date"
                type="date"
                value={formData.estimated_completion_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (isEditMode ? 'Update Project' : 'Create Project')}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/school-projects/projects')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default SchoolProjectForm;